<script lang="ts">
  import { onMount } from 'svelte'
  import { fade } from 'svelte/transition'
  import SiteLayout from '~/layouts/SiteLayout.svelte'
  import CategoryPlaceholderIcon from '~/components/site/CategoryPlaceholderIcon.svelte'
  import { siteContainerClass, siteBtnPrimaryClass, siteBtnOutlineClass } from '~/app/site-theme'

  interface Category {
    id: number
    name: string
    slug: string | null
    productsCount: number
  }

  interface HeroSlide {
    id: number
    title: string
    label: string | null
    imageUrl: string
  }

  type FallbackHeroSlide = {
    id: string
    title: string
    label: string
    kind: 'field' | 'harvest'
  }

  interface Props {
    categories: Category[]
    heroSlides: HeroSlide[]
  }

  let { categories, heroSlides = [] }: Props = $props()

  let activeHeroSlide = $state(0)
  let farmPhotoVisible = $state(true)

  const HERO_SLIDE_INTERVAL_MS = 4200

  const oldHomeText =
    'На нашей ферме мы выращиваем более 35 различных овощных культур. Вся наша продукция выращивается в грунте без применения ускорителей роста, что позволяет ей приобретать самые лучшие вкусовые качества и текстуру. Это в свою очередь высоко ценят шеф-повара лучших ресторанов России, и мы очень гордимся работой с ними.'

  const fallbackHeroSlides: FallbackHeroSlide[] = [
    { id: 'field', title: 'Овощи в грунте', label: '35+ культур', kind: 'field' },
    { id: 'harvest', title: 'Свежий урожай', label: 'Подмосковье', kind: 'harvest' },
  ]

  const visibleHeroSlides = $derived<(HeroSlide | FallbackHeroSlide)[]>(
    heroSlides.length > 0 ? heroSlides : fallbackHeroSlides
  )
  const activeSlide = $derived(visibleHeroSlides[activeHeroSlide] ?? visibleHeroSlides[0])

  onMount(() => {
    if (visibleHeroSlides.length <= 1) return

    const intervalId = window.setInterval(() => {
      activeHeroSlide = (activeHeroSlide + 1) % visibleHeroSlides.length
    }, HERO_SLIDE_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  })

  const features = [
    {
      id: 'growth',
      title: 'Без ускорителей роста',
      text: 'Только земля, солнце и время. Мы не используем стимуляторы, чтобы сохранить настоящий вкус',
    },
    {
      id: 'crops',
      title: '35+ культур',
      text: 'Капусты, корнеплоды, перцы, зелень и многое другое — широкий сезонный ассортимент',
    },
    {
      id: 'delivery',
      title: 'Доставка в Подмосковье',
      text: 'Привозим свежие овощи прямо с поля — ресторанам и частным клиентам',
    },
    {
      id: 'clients',
      title: 'Для дома и бизнеса',
      text: 'Овощи подходят для ресторанной кухни, магазинов и семейного стола',
    },
  ]
</script>

<SiteLayout
  title="Суперфуд Фарм — фермерские овощи Подмосковье"
  description="Более 35 овощных культур в грунте без ускорителей роста. Доставка ресторанам и частным покупателям в Подмосковье."
>
  <!-- HERO -->
  <section class="relative isolate w-full max-w-full overflow-hidden border-b border-[rgba(122,181,71,0.12)]">
    <div class="absolute inset-0 -z-10" aria-hidden="true">
      {#key activeSlide.id}
        <div class="absolute inset-0" transition:fade={{ duration: 900 }}>
          {#if 'imageUrl' in activeSlide && activeSlide.imageUrl}
            <img
              src={activeSlide.imageUrl}
              alt={activeSlide.title}
              class="h-full w-full object-cover"
              loading="eager"
            />
          {:else if activeSlide.kind === 'field'}
            <svg class="h-full w-full overflow-hidden object-cover" viewBox="0 0 1440 820" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="1440" height="820" fill="#081009" />
              <circle cx="1120" cy="145" r="90" fill="#21351f" />
              <circle cx="1152" cy="124" r="78" fill="#081009" />
              {#each [0, 1, 2, 3, 4, 5, 6, 7] as row (row)}
                <path d={`M0 ${520 + row * 46} C320 ${430 + row * 20} 720 ${650 + row * 12} 1440 ${520 + row * 48}`} stroke={row % 2 === 0 ? '#1d351b' : '#284422'} stroke-width="28" />
              {/each}
              {#each [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as leaf (leaf)}
                <g transform={`translate(${160 + leaf * 98}, ${560 + (leaf % 3) * 42})`}>
                  <path d="M10 28C-4 10 10-4 24 10C40-2 54 14 42 30C30 48 18 46 10 28Z" fill="#7AB547" opacity="0.45" />
                </g>
              {/each}
            </svg>
          {:else}
            <svg class="h-full w-full overflow-hidden object-cover" viewBox="0 0 1440 820" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="1440" height="820" fill="#091309" />
              <circle cx="990" cy="360" r="220" fill="#7AB547" opacity="0.1" />
              {#each [0, 1, 2, 3, 4, 5, 6, 7] as item (item)}
                <g transform={`translate(${660 + item * 82}, ${360 + (item % 2) * 70})`}>
                  <circle cx="34" cy="34" r="34" fill="#7AB547" opacity="0.2" />
                  <path d="M34 8C20 25 8 36 16 54C24 72 48 72 56 54C64 36 48 25 34 8Z" fill="#7AB547" opacity="0.55" />
                </g>
              {/each}
            </svg>
          {/if}
        </div>
      {/key}
      <div class="absolute inset-0 bg-[linear-gradient(90deg,rgba(9,16,10,0.78)_0%,rgba(9,16,10,0.66)_42%,rgba(9,16,10,0.28)_100%)]"></div>
      <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(122,181,71,0.08)_0%,transparent_52%)]"></div>
    </div>

    <div class="{siteContainerClass} relative flex min-h-[680px] items-center py-16 lg:py-20">
      <div class="relative z-10 max-w-2xl">
        <span
          class="mb-6 inline-flex items-center gap-2 rounded-full border border-[rgba(122,181,71,0.35)] bg-[#7AB547]/12 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#9dd36e]"
        >
          <span class="size-1.5 rounded-full bg-[#7AB547] animate-pulse"></span>
          Суперфуд Фарм · Подмосковье
        </span>

        <h1 class="text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
          Фермерские овощи<br />
          <span class="block text-3xl text-[#7AB547] sm:text-4xl lg:text-5xl">
            для ресторанов и дома
          </span>
        </h1>

        <p class="mt-6 max-w-2xl text-base leading-8 text-[#f4f8f1] sm:text-lg">
          {oldHomeText}
        </p>

        <div class="mt-8 flex flex-wrap gap-3">
          <a href="/catalog" class={siteBtnPrimaryClass}>
            <svg class="size-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/>
            </svg>
            Смотреть каталог
          </a>
          <a href="/about" class={siteBtnOutlineClass}>О ферме</a>
        </div>

        <div class="mt-12 flex flex-wrap items-center gap-6">
          <div>
            <p class="text-3xl font-bold text-white">35+</p>
            <p class="text-xs font-medium uppercase tracking-widest text-[#dfe8d8]">овощных культур</p>
          </div>
          <div class="h-10 w-px bg-[rgba(122,181,71,0.25)]"></div>
          <div>
            <p class="text-3xl font-bold text-white">100%</p>
            <p class="text-xs font-medium uppercase tracking-widest text-[#dfe8d8]">выращено в грунте</p>
          </div>
          <div class="h-10 w-px bg-[rgba(122,181,71,0.25)]"></div>
          <div>
            <p class="text-3xl font-bold text-white">Без</p>
            <p class="text-xs font-medium uppercase tracking-widest text-[#dfe8d8]">ускорителей роста</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- КАТЕГОРИИ -->
  <section class="py-16 sm:py-20">
    <div class="{siteContainerClass}">
      <div class="mb-10 flex items-end justify-between">
        <div>
          <p class="mb-2 text-xs font-semibold uppercase tracking-widest text-[#b9cdb0]">
            Что выращиваем
          </p>
          <h2 class="text-2xl font-bold text-white sm:text-3xl">Категории</h2>
        </div>
        <a
          href="/catalog"
          class="hidden text-sm font-medium text-[#7AB547] transition-colors hover:text-[#6ba03e] sm:block"
        >
          Весь каталог →
        </a>
      </div>

      <ul class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {#each categories as cat (cat.id)}
          <li class="h-full">
            <a
              href={cat.slug ? `/catalog#cat-${cat.slug}` : '/catalog'}
              class="group flex h-full min-h-40 flex-col items-center justify-center gap-4 rounded-2xl border border-[rgba(122,181,71,0.15)] bg-[#111d12] p-6 text-center transition-all hover:border-[rgba(122,181,71,0.4)] hover:bg-[#182619] hover:shadow-xl hover:shadow-[#7AB547]/8"
            >
              <div class="size-14 rounded-xl [&>svg]:size-full">
                <CategoryPlaceholderIcon name={cat.name} />
              </div>
              <div>
                <p class="font-semibold leading-snug text-white transition-colors group-hover:text-[#dff2d5]">
                  {cat.name}
                </p>
                <p class="mt-1 text-xs text-[#dfe8d8]">{cat.productsCount} позиций</p>
              </div>
            </a>
          </li>
        {/each}
      </ul>

      <div class="mt-6 text-center sm:hidden">
        <a href="/catalog" class="text-sm font-medium text-[#7AB547]">Весь каталог →</a>
      </div>
    </div>
  </section>

  <!-- О ФЕРМЕ -->
  <section class="border-t border-[rgba(122,181,71,0.1)] py-16 sm:py-20">
    <div class="{siteContainerClass}">
      <div class="grid gap-10 lg:grid-cols-2 lg:items-center">
        <div
          class="relative aspect-4/3 overflow-hidden rounded-2xl border border-[rgba(122,181,71,0.2)] bg-[#111d12]"
        >
          {#if farmPhotoVisible}
            <img
              src="/images/site/farm-photo.png"
              alt="Ферма Суперфуд Фарм"
              class="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              onerror={() => (farmPhotoVisible = false)}
            />
          {:else}
            <svg
              class="absolute inset-0 h-full w-full"
              viewBox="0 0 640 480"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="Наша ферма"
            >
              <rect width="640" height="480" fill="#0a1409" />
              <defs>
                <radialGradient id="sky" cx="50%" cy="30%" r="70%">
                  <stop offset="0%" stop-color="#182619" />
                  <stop offset="100%" stop-color="#0a1409" />
                </radialGradient>
              </defs>
              <rect width="640" height="480" fill="url(#sky)" />
              <circle cx="500" cy="80" r="40" fill="#1e3020" />
              <circle cx="515" cy="70" r="35" fill="#09100a" />
              {#each Array(20) as _, i (i)}
                <circle
                  cx={Math.sin(i * 1.7) * 280 + 320}
                  cy={Math.cos(i * 2.3) * 80 + 100}
                  r="1.5"
                  fill="#7AB547"
                  opacity="0.4"
                />
              {/each}
              {#each Array(8) as _, i (i)}
                <rect
                  x="0"
                  y={280 + i * 26}
                  width="640"
                  height="18"
                  fill={i % 2 === 0 ? '#111d12' : '#162215'}
                  rx="2"
                />
              {/each}
              {#each Array(12) as _, i (i)}
                <g transform="translate({i * 55 + 10}, {290 + (i % 3) * 26})">
                  <rect x="4" y="6" width="3" height="12" fill="#5a3a1a" rx="1" />
                  <ellipse cx="5" cy="6" rx="8" ry="10" fill="#7AB547" opacity="0.7" />
                </g>
              {/each}
              <text
                x="320"
                y="250"
                text-anchor="middle"
                font-family="sans-serif"
                font-size="16"
                fill="#7AB547"
                opacity="0.5"
              >
                Здесь будет фото фермы
              </text>
            </svg>
          {/if}
        </div>

        <div>
          <p class="mb-3 text-xs font-semibold uppercase tracking-widest text-[#b9cdb0]">
            О ферме
          </p>
          <h2 class="text-2xl font-bold text-white sm:text-3xl">
            Суперфуд Фарм
          </h2>
          <p class="mt-4 leading-8 text-[#f4f8f1]">
            {oldHomeText}
          </p>
          <div class="mt-8 flex gap-3">
            <a href="/about" class={siteBtnOutlineClass}>Подробнее о ферме</a>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- FEATURES / ПРЕИМУЩЕСТВА -->
  <section class="border-t border-[rgba(122,181,71,0.1)] py-16 sm:py-20">
    <div class="{siteContainerClass}">
      <div class="mb-12 text-center">
        <p class="mb-2 text-xs font-semibold uppercase tracking-widest text-[#b9cdb0]">
          Почему мы
        </p>
        <h2 class="text-2xl font-bold text-white sm:text-3xl">Наши преимущества</h2>
      </div>
      <ul class="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {#each features as f (f.id)}
          <li
            class="rounded-2xl border border-[rgba(122,181,71,0.15)] bg-[#111d12] p-6 transition-all hover:border-[rgba(122,181,71,0.3)] hover:shadow-lg"
          >
            <div
              class="mb-4 flex size-11 items-center justify-center rounded-xl bg-[#7AB547]/15 text-[#7AB547] [&>svg]:size-5"
            >
              <svg fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.745 3.745 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.745 3.745 0 013.296-1.043A3.745 3.745 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.745 3.745 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z"/>
              </svg>
            </div>
            <p class="font-semibold text-white">{f.title}</p>
            <p class="mt-2 text-sm leading-relaxed text-[#dfe8d8]">{f.text}</p>
          </li>
        {/each}
      </ul>
    </div>
  </section>

  <!-- CTA-баннер -->
  <section class="py-12 sm:py-16">
    <div class="{siteContainerClass}">
      <div
        class="relative overflow-hidden rounded-2xl border border-[rgba(122,181,71,0.25)] bg-[#111d12] p-8 sm:p-12"
      >
        <div
          class="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_0%_100%,rgba(122,181,71,0.1)_0%,transparent_50%)]"
          aria-hidden="true"
        ></div>
        <div class="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 class="text-xl font-bold text-white sm:text-2xl">
              Готовы к сотрудничеству?
            </h2>
            <p class="mt-2 text-sm text-[#dfe8d8]">
              Зарегистрируйтесь и оформляйте заказы онлайн. Частным клиентам и ресторанам.
            </p>
          </div>
          <div class="flex shrink-0 flex-wrap gap-3">
            <a href="/register" class={siteBtnPrimaryClass}>Стать клиентом</a>
            <a href="/contacts" class={siteBtnOutlineClass}>Связаться</a>
          </div>
        </div>
      </div>
    </div>
  </section>
</SiteLayout>
